// C2 마물 전투 연출 팩(VFX 쪽): 몹 직군 공격 연출 + 정예 접두 상시 표시. 계약은 shared/mobRoles.ts(이벤트 Ev.Mob*, MobsView.role/eliteAffix/shieldFrac, GameView.mobShots)뿐이다.
//
//   MobSwing    근접 휘두름 궤적(아크 모듈 재사용): 몹 크기에 비례, 정예는 굵고 길게, 지역 원소 색
//   MobDash     돌진 먼지 띠(몹을 따라가며 발밑 흙먼지 + 바람 줄) + 끝에 흙먼지 폭
//   MobShot     투척 출발 연기 / view.mobShots → 매 프레임 머리(회전 별)·꼬리·불똥(포물선 높이는 게임 y 그대로)
//   MobShotHit  착탄 폭발: 링(반경 = 판정 반경) + 파편 + 불똥 + 먼지
//   MobSummon   b=0 바닥 룬(점점 밝아짐) / b=1 기둥 + 룬 풀림 + 국소 섬광(FlashLimiter 경유)
//   MobAffix    c=0 부여 신호 / c=1 보호막 깨짐 파편 / c=2 분열 폭발(균열 번쩍)
//   정예 접두 오라 4종: 신속 = 뒤로 흐르는 바람 줄, 보호막 = 잔량 비례 구체(깨지면 사라짐), 분열 = 발밑에서 맥동하는 균열. 접두 0이면 아무것도 안 그린다(기존 정예 표시 유지)
//
// 풀은 생성 때 한 번 할당(모듈 3 + MotePool 3 + 줄/머리/구체/바닥 판 4)하고 프레임 루프·이벤트 처리에서 객체를 만들지 않는다. 가득 차면 가장 오래된 것을 대체한다.
// 섬광 없음: 전체 화면 효과가 없고, 소환 완료의 국소 번쩍임(큰 점 하나)만 FlashLimiter.admitSoft(1초 3회·334ms 간격 예산)를 지난 때만 쓴다.
import * as THREE from 'three/webgpu';
import { attribute, float, length, mix, smoothstep, uv, vec2, vec3 } from 'three/tsl';
import { MOB_STATS } from '../../shared/config';
import { Ev, type GameEvent } from '../../shared/events';
import { MobState, type GameView } from '../../shared/types';
import { MOB_SHOT_CAPACITY } from '../../shared/mobRoles';
import type { GameClocks } from '../../render/clock';
import { TARGET_H } from '../../render/mobs';
import { ArcModule } from '../modules/arc';
import { createNoiseTexture } from '../modules/noise';
import { PillarModule } from '../modules/pillar';
import { RingModule } from '../modules/ring';
import { fillParams, makeParams, type FxParams } from '../modules/types';
import type { FlashLimiter } from '../flash';
import { MotePool } from '../regionMobFx';
import { AFFIX_COLOR, AFFIX_CORE, paletteOfMap, type FxPalette } from './palette';
import { BillboardPool, DECAL_CRACK, DECAL_RUNE, DECAL_SHADOW, DecalPool, ShieldPool, StretchPool } from './pools';

/**
 * 예산(품질 particleScale 1 기준, 0.5 단계에서 입자 수·동시 칸이 절반). 드로우콜은 상한 11 고정(스케일과 무관).
 * 드로우콜: 모듈 아크·링·기둥 3 + 먼지·불똥·파편 3 + 줄 1 + 머리 1 + 보호막 1 + 바닥 판 1(룬·균열·긁힘·그림자 점이 한 메시).
 */
export const MOB_FX_BUDGET = {
  drawCalls: 11,
  /** 모듈 동시 칸(아크·링·기둥) */
  arc: 6, ring: 6, pillar: 3,
  /** MotePool 동시 입자 */
  mist: 72, glint: 80, shard: 56,
  /** 줄 풀: 시뮬 칸(바람 줄·돌진 줄) + 즉시 칸(투사체 꼬리 굵은 것·가는 것 = 투사체당 2) */
  stretch: 64, stretchShots: MOB_SHOT_CAPACITY * 2,
  /** 투사체 머리(후광 + 회전 별 + 흰 심 = 투사체당 3) */
  heads: MOB_SHOT_CAPACITY * 3,
  /** 바닥 판: 지속(소환 룬·분열 균열 번쩍) + 긁힘(돌진 경로 자국, 스스로 사라짐) + 즉시(정예 균열 + 투사체 착탄 그림자 점) */
  decalPersistent: 6, decalStreak: 12, decalImmediate: 16,
  /** 보호막 구체·균열·바람 줄을 동시에 그릴 정예 수(카메라/영웅 가까운 순이 아니라 번호순, 범위 안만) */
  affixMobs: 8, affixRange: 24,
  /** 동시 돌진 추적 수 · 소환 룬 추적 수 · 룬이 풀리지 않을 때 자동으로 푸는 시간(초) */
  dashes: 8, runes: 6, runeTimeout: 4,
  /** 초당 방출: 돌진 먼지 · 돌진 줄 · 투사체 불똥 · 신속 바람 줄(몹 한 마리). 돌진 긁힘은 이동 거리(m) 간격으로 하나 */
  dashDustPerSec: 36, dashStreakPerSec: 16, shotSparkPerSec: 40, windPerSec: 20, scuffSpacing: 1.3,
  /** 이벤트 한 번의 폭발 입자 수 */
  hit: { shard: 10, glint: 6, mist: 4 },
  split: { shard: 12, glint: 6, mist: 3 },
  shieldBreak: { shard: 14, glint: 8 },
} as const;

/**
 * MotePool은 색 채널을 /255한 값을 그대로(선형으로) 넣어 화면에선 밝게 들뜬다. 어두운 먼지·돌조각이 회색으로 뜨지 않게 표시색(sRGB)을 선형값으로 내려 준다.
 */
const lin = (c: number): number => {
  const f = (v: number): number => Math.round(Math.pow(v / 255, 2.2) * 255);
  return (f((c >> 16) & 255) << 16) | (f((c >> 8) & 255) << 8) | f(c & 255);
};

type Ev9 = GameEvent;
const INACTIVE = (s: number): boolean => s === MobState.Inactive || s === MobState.Dying || s === MobState.Spawning;
const SWING_EMPTY = {};

export interface MobFxStats {
  spawned: number; replaced: number; flares: number; flareDenied: number;
  swings: number; dashes: number; shots: number; hits: number; runes: number; affix: number;
}

export class MobAttackFx {
  readonly group = new THREE.Group();
  private readonly noise = createNoiseTexture();
  private readonly arc = new ArcModule(this.noise, MOB_FX_BUDGET.arc);
  private readonly ring = new RingModule(this.noise, MOB_FX_BUDGET.ring);
  private readonly pillar = new PillarModule(this.noise, MOB_FX_BUDGET.pillar);
  private readonly mist = new MotePool(MOB_FX_BUDGET.mist, 'normal', 'disc', 19);
  private readonly glint = new MotePool(MOB_FX_BUDGET.glint, 'add', 'dot', 25);
  private readonly shard = new MotePool(MOB_FX_BUDGET.shard, 'normal', 'diamond', 20);
  private readonly stretch = new StretchPool(MOB_FX_BUDGET.stretch, MOB_FX_BUDGET.stretchShots, 23);
  private readonly heads = new BillboardPool(MOB_FX_BUDGET.heads, 26);
  private readonly shield = new ShieldPool(MOB_FX_BUDGET.affixMobs, 21);
  private readonly decal = new DecalPool(MOB_FX_BUDGET.decalPersistent, MOB_FX_BUDGET.decalImmediate, 7, MOB_FX_BUDGET.decalStreak);
  private readonly p: FxParams = makeParams();
  private scale: number;
  private reduceFlash = false;
  private seed = 0x1f3a5c7;
  private nowMs = 0;
  private time = 0;
  private flip = 1;
  private pal: FxPalette = paletteOfMap('r1');
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly fwd = new THREE.Vector3();
  // 돌진 추적(몹 번호 → 방향·진행)
  private readonly dashMob = new Int16Array(MOB_FX_BUDGET.dashes).fill(-1);
  private readonly dashAng = new Float32Array(MOB_FX_BUDGET.dashes);
  private readonly dashAge = new Float32Array(MOB_FX_BUDGET.dashes);
  private readonly dashEnd = new Uint8Array(MOB_FX_BUDGET.dashes);
  private readonly dashAccDust = new Float32Array(MOB_FX_BUDGET.dashes);
  private readonly dashAccStreak = new Float32Array(MOB_FX_BUDGET.dashes);
  private readonly dashLastX = new Float32Array(MOB_FX_BUDGET.dashes);
  private readonly dashLastZ = new Float32Array(MOB_FX_BUDGET.dashes);
  private readonly dashAccDist = new Float32Array(MOB_FX_BUDGET.dashes);
  // 소환 룬 추적(몹 번호 → 바닥 판 칸)
  private readonly runeMob = new Int16Array(MOB_FX_BUDGET.runes).fill(-1);
  private readonly runeSlot = new Int16Array(MOB_FX_BUDGET.runes).fill(-1);
  private readonly runeAge = new Float32Array(MOB_FX_BUDGET.runes);
  // 몹·투사체별 방출 누적
  private readonly windAcc = new Float32Array(128);
  private readonly shotAcc = new Float32Array(MOB_SHOT_CAPACITY);
  private readonly shotGen = new Uint16Array(MOB_SHOT_CAPACITY);
  readonly stats: MobFxStats = { spawned: 0, replaced: 0, flares: 0, flareDenied: 0, swings: 0, dashes: 0, shots: 0, hits: 0, runes: 0, affix: 0 };

  /** @param particleScale 품질 단계의 입자 배율(1 / 0.75 / 0.5). setScale로 바꿀 수 있다 */
  constructor(particleScale: number, private readonly flash: FlashLimiter | null = null) {
    this.scale = Math.max(0.25, particleScale);
    this.group.name = 'mob-attack-fx';
    this.styleMist();
    this.group.add(this.decal.mesh, this.arc.group, this.ring.group, this.pillar.group, this.mist.mesh, this.shard.mesh, this.shield.mesh, this.stretch.mesh, this.glint.mesh, this.heads.mesh);
  }

  /**
   * 먼지 덩이(mist 풀)를 윤곽 있는 만화 덩이로: 가장자리가 또렷하고(부드러운 번짐 대신) 테두리 쪽을 어둡게 눌러, 같은 색이 바닥 색과 비슷해도 윤곽으로 읽힌다.
   * 풀은 이 팩만 쓰므로 재질을 여기서 바꿔도 다른 연출에 번지지 않는다(셰이더 변형 한 개, 드로우콜 그대로).
   */
  private styleMist(): void {
    const m = this.mist.mesh.material as THREE.MeshBasicNodeMaterial;
    const d = length(vec2(uv().x.sub(0.5).mul(2), uv().y.sub(0.5).mul(2)));
    m.opacityNode = float(1).sub(smoothstep(0.7, 1.0, d)).mul(attribute('aAlpha', 'float'));
    // 가운데 1.0 → 가장자리 0.35(주색의 어두운 테두리)
    const rim = float(1).sub(smoothstep(0.45, 0.95, d));
    m.colorNode = vec3(1, 1, 1).mul(mix(float(0.35), float(1), rim));
    m.needsUpdate = true;
  }

  setScale(s: number): void { this.scale = Math.max(0.25, s); }
  setReduceFlash(on: boolean): void {
    this.reduceFlash = on;
    const g = on ? 0.7 : 1;
    this.arc.setGain(g); this.ring.setGain(g); this.pillar.setGain(g);
  }

  private rnd(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }
  /** 입자 수 배율(최소 1) */
  private cnt(base: number): number { return Math.max(1, Math.round(base * this.scale)); }
  private cap(n: number): number { return Math.max(4, Math.floor(n * this.scale)); }

  /** 살아 있는 칸 수(QA·시험) */
  get active(): { mist: number; glint: number; shard: number; stretch: number; arcs: number; rings: number; pillars: number; decals: number; heads: number; shields: number } {
    return {
      mist: this.mist.n, glint: this.glint.n, shard: this.shard.n, stretch: this.stretch.n + this.stretch.im,
      arcs: this.arc.activeCount, rings: this.ring.activeCount, pillars: this.pillar.activeCount,
      decals: this.decal.active + this.decal.im, heads: this.heads.im, shields: this.shield.im,
    };
  }
  /** 지금 이 팩이 그리는 드로우콜(MOB_FX_BUDGET.drawCalls 이하) */
  get drawCalls(): number {
    let n = this.arc.drawCalls + this.ring.drawCalls + this.pillar.drawCalls;
    for (const m of [this.mist.mesh, this.glint.mesh, this.shard.mesh, this.stretch.mesh, this.heads.mesh, this.shield.mesh, this.decal.mesh]) if (m.count > 0) n++;
    return n;
  }

  // ---- 입자 도우미 -------------------------------------------------------------------------------

  private mote(pool: MotePool, capN: number, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, w0: number, w1: number, color: number, alpha: number, grav: number, drag: number, sway: number): void {
    if (pool.n >= capN) { pool.evictOldest(); this.stats.replaced++; }
    pool.spawn(x, y, z, vx, vy, vz, life, w0, w1, color, alpha, grav, drag, sway, false);
    this.stats.spawned++;
  }
  private dust(x: number, y: number, z: number, vx: number, vz: number, color: number, size: number, alpha: number): void {
    this.mote(this.mist, this.cap(MOB_FX_BUDGET.mist), x, y, z, vx, 0.25 + this.rnd() * 0.35, vz, 0.5 + this.rnd() * 0.35, 0.28 * size, 0.85 * size, lin(color), alpha, -0.1, 1.6, 0.1);
  }
  private spark(x: number, y: number, z: number, vx: number, vy: number, vz: number, color: number, size: number, life: number): void {
    const glow = this.reduceFlash ? 0.55 : 1;
    this.mote(this.glint, this.cap(MOB_FX_BUDGET.glint), x, y, z, vx, vy, vz, life, size, size * 0.3, color, 0.9 * glow, 1.2, 1.4, 0);
  }
  private chip(x: number, y: number, z: number, vx: number, vy: number, vz: number, color: number, size: number): void {
    this.mote(this.shard, this.cap(MOB_FX_BUDGET.shard), x, y, z, vx, vy, vz, 0.45 + this.rnd() * 0.3, size, size * 0.4, color, 0.95, 12, 1.3, 0);
  }

  /** 한 점 폭발: 파편 · 불똥 · 먼지. spread = 수평 속도 배율, up = 위로 튀는 힘 */
  private burst(x: number, y: number, z: number, nShard: number, nGlint: number, nMist: number, spread: number, up: number, shardColor: number, glintColor: number, dustColor: number, size: number): void {
    for (let i = 0, n = this.cnt(nShard); i < n; i++) {
      const a = this.rnd() * Math.PI * 2, s = (1.6 + this.rnd() * 2.6) * spread;
      this.chip(x, y, z, Math.sin(a) * s, up * (0.7 + this.rnd() * 0.8), Math.cos(a) * s, shardColor, (0.09 + this.rnd() * 0.06) * size);
    }
    for (let i = 0, n = this.cnt(nGlint); i < n; i++) {
      const a = this.rnd() * Math.PI * 2, s = (0.8 + this.rnd() * 1.8) * spread;
      this.spark(x, y + 0.1, z, Math.sin(a) * s, up * 0.5 + this.rnd(), Math.cos(a) * s, glintColor, (0.16 + this.rnd() * 0.1) * size, 0.3 + this.rnd() * 0.25);
    }
    for (let i = 0, n = this.cnt(nMist); i < n; i++) {
      const a = this.rnd() * Math.PI * 2, s = (0.5 + this.rnd() * 0.9) * spread;
      this.dust(x + Math.sin(a) * 0.2, 0.15, z + Math.cos(a) * 0.2, Math.sin(a) * s, Math.cos(a) * s, dustColor, size, 0.34);
    }
  }

  /** 모듈 파라미터 기본값으로 채운 공용 객체(이벤트마다 새 객체를 만들지 않는다) */
  private params(x: number, y: number, z: number, yaw: number): FxParams {
    return fillParams(this.p, SWING_EMPTY, x, y, z, yaw);
  }

  // ---- 이벤트 -------------------------------------------------------------------------------------

  onEvent(e: Ev9, mapId: string): void {
    switch (e.type) {
      case Ev.MobSwing: case Ev.MobDash: case Ev.MobShot: case Ev.MobShotHit: case Ev.MobSummon: case Ev.MobAffix:
        this.pal = paletteOfMap(mapId);
        break;
      default: return;
    }
    switch (e.type) {
      case Ev.MobSwing: this.onSwing(e); break;
      case Ev.MobDash: this.onDash(e); break;
      case Ev.MobShot: this.onShot(e); break;
      case Ev.MobShotHit: this.onShotHit(e); break;
      case Ev.MobSummon: this.onSummon(e); break;
      case Ev.MobAffix: this.onAffix(e); break;
      default: break;
    }
  }

  private onSwing(e: Ev9): void {
    this.stats.swings++;
    const pal = this.pal, kind = e.b | 0, elite = e.d === 1;
    const size = (TARGET_H[kind] ?? 1.5) / 1.5;
    const range = MOB_STATS[kind]?.attackRange ?? 1.4;
    const reach = Math.min(2.7, Math.max(1.7, range * 0.85 + 0.6)) * (elite ? 1.15 : 1);
    const p = this.params(e.x, 0.95 * size, e.z, e.c);
    this.flip = -this.flip;
    p.ahead = 0.25; p.roll = 0.3 + 0.12 * this.rnd(); p.length = reach;
    p.width = (0.75 + 0.35 * size) * (elite ? 1.45 : 1);
    p.start = this.flip > 0 ? -1.05 : 1.05; p.sweep = 2.1 * this.flip;
    p.life = elite ? 0.34 : 0.26; p.color = pal.main; p.color2 = pal.core; p.intensity = elite ? 2.1 : 1.5; p.seed = this.rnd();
    this.arc.spawn(p);
    // 휘두름 끝에서 튀는 불똥(정예는 더 많이)
    const sx = Math.sin(e.c), sz = Math.cos(e.c), tipX = e.x + sx * reach * 0.9, tipZ = e.z + sz * reach * 0.9;
    for (let i = 0, n = this.cnt(elite ? 5 : 3); i < n; i++) {
      const a = e.c + (this.rnd() - 0.5) * 1.6, s = 1.5 + this.rnd() * 2;
      this.spark(tipX, 0.9 * size, tipZ, Math.sin(a) * s, 0.4 + this.rnd(), Math.cos(a) * s, pal.core, 0.14 + this.rnd() * 0.08, 0.22 + this.rnd() * 0.12);
    }
  }

  private onDash(e: Ev9): void {
    const pal = this.pal, mob = e.a | 0;
    let slot = -1, free = -1, oldest = 0, oldestAge = -1;
    for (let i = 0; i < this.dashMob.length; i++) {
      if (this.dashMob[i] === mob) slot = i;
      else if (this.dashMob[i] < 0) { if (free < 0) free = i; }
      else if (this.dashAge[i] > oldestAge) { oldestAge = this.dashAge[i]; oldest = i; }
    }
    if (e.b === 1) {
      if (slot >= 0) this.dashEnd[slot] = 1;
      return;
    }
    this.stats.dashes++;
    if (slot < 0) slot = free >= 0 ? free : oldest;
    this.dashMob[slot] = mob; this.dashAng[slot] = e.c; this.dashAge[slot] = 0; this.dashEnd[slot] = 0;
    this.dashAccDust[slot] = 0; this.dashAccStreak[slot] = 0; this.dashAccDist[slot] = 0; this.dashLastX[slot] = e.x; this.dashLastZ[slot] = e.z;
    // 출발 흙먼지: 발밑에서 뒤로 크게 터진다(지역 바닥과 명도가 반대인 색 + 어두운 테두리) + 출발점 긁힘 + 어두운 돌조각
    const bx = -Math.sin(e.c), bz = -Math.cos(e.c);
    for (let i = 0, n = this.cnt(6); i < n; i++) this.dust(e.x, 0.12, e.z, bx * (0.8 + this.rnd()) + (this.rnd() - 0.5), bz * (0.8 + this.rnd()) + (this.rnd() - 0.5), pal.dust, 1.9, 0.78);
    for (let i = 0, n = this.cnt(4); i < n; i++) this.chip(e.x, 0.1, e.z, bx * 2 + (this.rnd() - 0.5) * 2, 1.5 + this.rnd(), bz * 2 + (this.rnd() - 0.5) * 2, lin(pal.scuff), 0.12);
    this.decal.spawnStreak(e.x + bx * 0.5, e.z + bz * 0.5, -bx, -bz, 0.9, 0.6, pal.scuff, 0.85, 1.2, this.rnd());
  }

  private onShot(e: Ev9): void {
    this.stats.shots++;
    const pal = this.pal;
    for (let i = 0, n = this.cnt(4); i < n; i++) {
      const a = e.c + (this.rnd() - 0.5) * 1.2, s = 1 + this.rnd() * 1.5;
      this.spark(e.x, e.y, e.z, Math.sin(a) * s, 0.3 + this.rnd(), Math.cos(a) * s, pal.main, 0.16, 0.3);
    }
    this.dust(e.x, Math.max(0.3, e.y * 0.7), e.z, Math.sin(e.c) * 0.5, Math.cos(e.c) * 0.5, pal.dust, 0.7, 0.3);
  }

  private onShotHit(e: Ev9): void {
    this.stats.hits++;
    const pal = this.pal, R = Math.max(0.6, e.c);
    const p = this.params(e.x, 0, e.z, 0);
    p.length = R; p.from = 0.25; p.width = Math.min(0.7, R * 0.35); p.life = 0.5; p.color = pal.main; p.color2 = pal.core; p.intensity = e.b === 1 ? 2.2 : 1.7; p.seed = this.rnd();
    this.ring.spawn(p);
    const H = MOB_FX_BUDGET.hit;
    this.burst(e.x, 0.25, e.z, H.shard, H.glint + (e.b === 1 ? 4 : 0), H.mist, 1, 3.2, pal.shard, pal.core, pal.dust, 1);
  }

  private onSummon(e: Ev9): void {
    const pal = this.pal, mob = e.a | 0, R = Math.max(0.8, e.d);
    if (e.b === 0) {
      this.stats.runes++;
      let slot = -1;
      for (let i = 0; i < this.runeMob.length; i++) if (this.runeMob[i] < 0) { slot = i; break; }
      if (slot < 0) { // 가득: 가장 오래된 룬을 풀고 대체
        let oa = -1;
        for (let i = 0; i < this.runeMob.length; i++) if (this.runeAge[i] > oa) { oa = this.runeAge[i]; slot = i; }
        this.decal.release(this.runeSlot[slot]);
      }
      this.runeMob[slot] = mob; this.runeAge[slot] = 0;
      this.runeSlot[slot] = this.decal.spawn(DECAL_RUNE, e.x, e.z, R, pal.main, Infinity, 1.4, this.rnd());
      return;
    }
    // 소환 완료: 룬 풀림 + 기둥 + 위로 오르는 불똥
    for (let i = 0; i < this.runeMob.length; i++) if (this.runeMob[i] === mob) { this.decal.release(this.runeSlot[i]); this.runeMob[i] = -1; this.runeSlot[i] = -1; }
    // 국소 번쩍임은 섬광 예산(1초 3회 · 334ms 간격)을 지난 때만. 섬광 약화 설정이면 건너뛴다
    let bright = false;
    if (!this.reduceFlash && this.flash) {
      bright = this.flash.admitSoft(this.nowMs);
      if (bright) this.stats.flares++; else this.stats.flareDenied++;
    }
    const p = this.params(e.x, 0, e.z, 0);
    p.length = 4.2; p.width = Math.min(1.1, R * 0.45); p.life = 0.85; p.color = pal.main; p.color2 = pal.core; p.intensity = bright ? 2.2 : 1.1; p.seed = this.rnd();
    this.pillar.spawn(p);
    const q = this.params(e.x, 0, e.z, 0);
    q.length = R; q.from = 0.35; q.width = 0.4; q.life = 0.5; q.color = pal.main; q.color2 = pal.core; q.intensity = 1.6; q.seed = this.rnd();
    this.ring.spawn(q);
    if (bright) this.spark(e.x, 1.1, e.z, 0, 0.2, 0, pal.core, 3.0, 0.2);
    for (let i = 0, n = this.cnt(10); i < n; i++) {
      const a = this.rnd() * Math.PI * 2, r = R * (0.2 + this.rnd() * 0.7);
      this.spark(e.x + Math.sin(a) * r, 0.1, e.z + Math.cos(a) * r, 0, 1.6 + this.rnd() * 1.8, 0, this.rnd() < 0.6 ? pal.main : pal.core, 0.14 + this.rnd() * 0.08, 0.6 + this.rnd() * 0.4);
    }
    this.dust(e.x, 0.15, e.z, 0, 0, pal.dust, 1.4, 0.3);
  }

  private onAffix(e: Ev9): void {
    this.stats.affix++;
    const aff = Math.max(1, Math.min(3, e.b | 0));
    const col = AFFIX_COLOR[aff], core = AFFIX_CORE[aff];
    if (e.c === 0) { // 부여: 작은 링 + 떠오르는 불똥
      const p = this.params(e.x, 0, e.z, 0);
      p.length = 1.3; p.from = 0.3; p.width = 0.28; p.life = 0.45; p.color = col; p.color2 = core; p.intensity = 1.3; p.seed = this.rnd();
      this.ring.spawn(p);
      for (let i = 0, n = this.cnt(5); i < n; i++) {
        const a = this.rnd() * Math.PI * 2, r = 0.4 + this.rnd() * 0.5;
        this.spark(e.x + Math.sin(a) * r, 0.2, e.z + Math.cos(a) * r, 0, 1 + this.rnd(), 0, col, 0.12, 0.6);
      }
    } else if (e.c === 1) { // 보호막 깨짐
      const p = this.params(e.x, 1.0, e.z, 0);
      p.length = 1.7; p.from = 0.4; p.width = 0.3; p.life = 0.38; p.color = col; p.color2 = core; p.intensity = 1.8; p.seed = this.rnd(); p.roll = 0;
      this.ring.spawn(p);
      const S = MOB_FX_BUDGET.shieldBreak;
      this.burst(e.x, 1.0, e.z, S.shard, S.glint, 0, 1.15, 2.2, col, core, col, 1.1);
    } else { // 분열 폭발
      this.decal.spawn(DECAL_CRACK, e.x, e.z, 1.9, col, 0.55, 0.08, this.rnd());
      const p = this.params(e.x, 0, e.z, 0);
      p.length = 2.2; p.from = 0.25; p.width = 0.5; p.life = 0.45; p.color = col; p.color2 = core; p.intensity = 1.9; p.seed = this.rnd();
      this.ring.spawn(p);
      const S = MOB_FX_BUDGET.split;
      this.burst(e.x, 0.6, e.z, S.shard, S.glint, S.mist, 1.1, 3, col, core, 0x4a1030, 1);
    }
  }

  // ---- 프레임 -------------------------------------------------------------------------------------

  /**
   * 매 프레임. 시계는 세계 시계(히트스톱 중에도 몹 클립과 같이 간다). camera는 빌보드 기저를 위해 필요하다.
   * 이벤트(onEvent)는 이 호출 직전에 처리되므로 이번 프레임부터 보인다.
   */
  update(view: GameView, alpha: number, clocks: GameClocks, camera: THREE.Camera): void {
    const dt = Math.max(0, clocks.worldDelta / 60);
    this.nowMs = clocks.realMs;
    this.time += dt;
    this.pal = paletteOfMap(view.map.id);
    camera.matrixWorld.extractBasis(this.right, this.up, this.fwd);
    const r = this.right, u = this.up, f = this.fwd;
    this.stretch.setView(f.x, f.y, f.z);
    this.heads.setBasis(r.x, r.y, r.z, u.x, u.y, u.z, f.x, f.y, f.z);

    // 1) 방출(이번 프레임 입자로 들어간다)
    this.emitDashes(view, alpha, dt);
    this.runeTimeouts(dt);
    this.emitAffixes(view, alpha, dt);
    this.emitShotSparks(view, alpha, dt);

    // 2) 진행 · 기록
    this.arc.update(dt); this.ring.update(dt); this.pillar.update(dt);
    this.mist.update(dt, r.x, r.y, r.z, u.x, u.y, u.z, f.x, f.y, f.z, this.time);
    this.glint.update(dt, r.x, r.y, r.z, u.x, u.y, u.z, f.x, f.y, f.z, this.time);
    this.shard.update(dt, r.x, r.y, r.z, u.x, u.y, u.z, f.x, f.y, f.z, this.time);
    this.stretch.update(dt);
    this.decal.update(dt);

    // 3) 즉시 모드(게임 뷰에서 매 프레임 읽는 것)
    this.stretch.begin(); this.heads.begin(); this.shield.begin(); this.decal.begin();
    this.drawAffixes(view, alpha);
    this.drawShots(view, alpha);
    this.stretch.end(); this.heads.end(); this.shield.end(); this.decal.end();
  }

  private mobX(m: GameView['mobs'], i: number, a: number): number { return m.prevX ? m.prevX[i] + (m.x[i] - m.prevX[i]) * a : m.x[i]; }
  private mobZ(m: GameView['mobs'], i: number, a: number): number { return m.prevZ ? m.prevZ[i] + (m.z[i] - m.prevZ[i]) * a : m.z[i]; }

  private emitDashes(view: GameView, alpha: number, dt: number): void {
    const B = MOB_FX_BUDGET, pal = this.pal, m = view.mobs;
    for (let s = 0; s < this.dashMob.length; s++) {
      const mob = this.dashMob[s];
      if (mob < 0) continue;
      const ang = this.dashAng[s], sx = Math.sin(ang), sz = Math.cos(ang);
      const gone = mob >= m.capacity || INACTIVE(m.state[mob]);
      const x = gone ? this.dashLastX[s] : this.mobX(m, mob, alpha), z = gone ? this.dashLastZ[s] : this.mobZ(m, mob, alpha);
      const pmx = this.dashLastX[s], pmz = this.dashLastZ[s];
      this.dashLastX[s] = x; this.dashLastZ[s] = z;
      this.dashAge[s] += dt;
      // 지나간 바닥에 긁힘 자국: 이동 거리 scuffSpacing마다 진행 방향으로 긴 흠집 줄 하나(1.2초 안에 사라진다)
      {
        const mx = x - pmx, mz = z - pmz, md = Math.hypot(mx, mz);
        if (md > 1e-4 && !gone) {
          this.dashAccDist[s] += md;
          const ux = mx / md, uz = mz / md, SP = B.scuffSpacing;
          while (this.dashAccDist[s] >= SP) {
            this.dashAccDist[s] -= SP;
            const back = this.dashAccDist[s] + SP * 0.5;
            this.decal.spawnStreak(x - ux * back, z - uz * back, ux, uz, SP * 0.65, 0.55, pal.scuff, 0.85, 1.2, this.rnd());
          }
        }
      }
      if (this.dashEnd[s] === 1 || gone || this.dashAge[s] > 1.4) {
        // 끝: 멈추는 흙먼지 폭 + 작은 링
        const p = this.params(x, 0, z, 0);
        p.length = 1.9; p.from = 0.3; p.width = 0.36; p.life = 0.4; p.color = pal.main; p.color2 = pal.core; p.intensity = 1.1; p.seed = this.rnd();
        this.ring.spawn(p);
        this.burst(x, 0.15, z, 6, 3, 6, 0.9, 2.2, lin(pal.scuff), pal.core, pal.dust, 1.8);
        this.dashMob[s] = -1; this.dashAge[s] = 0;
        continue;
      }
      if (dt <= 0) continue;
      this.dashAccDust[s] += dt * B.dashDustPerSec * this.scale;
      while (this.dashAccDust[s] >= 1) {
        this.dashAccDust[s] -= 1;
        const lat = (this.rnd() - 0.5) * 0.9;
        this.dust(x - sx * 0.3 + sz * lat, 0.1, z - sz * 0.3 - sx * lat, -sx * 0.5 + sz * lat * 0.6, -sz * 0.5 - sx * lat * 0.6, pal.dust, 1.7, 0.72);
        if (this.rnd() < 0.4) this.chip(x - sx * 0.2, 0.08, z - sz * 0.2, -sx * 1.5 + (this.rnd() - 0.5) * 1.5, 1.2 + this.rnd(), -sz * 1.5 + (this.rnd() - 0.5) * 1.5, lin(pal.scuff), 0.11);
      }
      this.dashAccStreak[s] += dt * B.dashStreakPerSec * this.scale;
      while (this.dashAccStreak[s] >= 1) {
        this.dashAccStreak[s] -= 1;
        const lat = (this.rnd() - 0.5) * 1.1, hy = 0.25 + this.rnd() * 0.9;
        this.replacedCheck(this.stretch.spawn(x + sz * lat, hy, z - sx * lat, sx, 0, sz, -sx * 1.2, 0, -sz * 1.2, 1.1 + this.rnd() * 0.9, 0.07, 0.22, this.rnd() < 0.5 ? pal.core : pal.main, 0.55, this.cap(B.stretch)));
      }
    }
  }

  private replacedCheck(replaced: boolean): void { this.stats.spawned++; if (replaced) this.stats.replaced++; }

  private runeTimeouts(dt: number): void {
    for (let i = 0; i < this.runeMob.length; i++) {
      if (this.runeMob[i] < 0) continue;
      this.runeAge[i] += dt;
      if (this.runeAge[i] > MOB_FX_BUDGET.runeTimeout || !this.decal.isUsed(this.runeSlot[i])) {
        this.decal.release(this.runeSlot[i]);
        this.runeMob[i] = -1; this.runeSlot[i] = -1;
      }
    }
  }

  private emitAffixes(view: GameView, alpha: number, dt: number): void {
    const m = view.mobs;
    if (!m.eliteAffix || dt <= 0) return;
    const B = MOB_FX_BUDGET, hero = view.hero;
    const cap = this.cap(B.affixMobs);
    const lim = Math.min(m.capacity, this.windAcc.length);
    let used = 0;
    for (let i = 0; i < lim && used < cap; i++) {
      if (INACTIVE(m.state[i]) || !m.elite[i] || m.eliteAffix[i] !== 1) continue;
      const x = this.mobX(m, i, alpha), z = this.mobZ(m, i, alpha);
      if (Math.hypot(x - hero.x, z - hero.z) > B.affixRange) continue;
      used++;
      // 이동 방향(없으면 바라보는 방향)의 반대로 흐르는 줄이 몸 둘레를 나선으로 감는다: 높이에 따라 위상이 돌아 줄이 소용돌이 모양으로 늘어선다
      const vx = m.prevX ? m.x[i] - m.prevX[i] : 0, vz = m.prevZ ? m.z[i] - m.prevZ[i] : 0;
      const sp = Math.hypot(vx, vz) * 60;
      let dx: number, dz: number;
      if (sp > 0.3) { dx = vx / (sp / 60); dz = vz / (sp / 60); } else { dx = Math.sin(m.facing[i]); dz = Math.cos(m.facing[i]); }
      this.windAcc[i] += dt * B.windPerSec * this.scale * (0.45 + Math.min(1, sp / 3.5) * 0.55);
      const h = TARGET_H[m.kind[i]] ?? 1.5, rad = 0.42 + h * 0.18;
      while (this.windAcc[i] >= 1) {
        this.windAcc[i] -= 1;
        const y = 0.2 + this.rnd() * h * 0.8;
        const ph = this.time * 8 + y * 5.5 + this.rnd() * 0.5;
        const cx = Math.cos(ph), cz = Math.sin(ph);
        // 줄 방향 = 뒤쪽 흐름 + 둘레 접선(나선), 머리가 앞
        let ax = -dx * 0.75 - cz * 0.85, az = -dz * 0.75 + cx * 0.85;
        const al = Math.hypot(ax, az) || 1; ax /= al; az /= al;
        this.replacedCheck(this.stretch.spawn(x + cx * rad, y, z + cz * rad, ax, 0, az, -dx * 2.0, 0, -dz * 2.0, 2.0 + this.rnd() * 1.4, 0.26, 0.42, this.rnd() < 0.55 ? AFFIX_COLOR[1] : AFFIX_CORE[1], 0.85, this.cap(B.stretch)));
      }
    }
  }

  private emitShotSparks(view: GameView, alpha: number, dt: number): void {
    const sh = view.mobShots;
    if (!sh || dt <= 0) return;
    const pal = this.pal;
    for (let i = 0; i < MOB_SHOT_CAPACITY; i++) {
      if (!sh.alive[i]) { this.shotAcc[i] = 0; continue; }
      if (this.shotGen[i] !== sh.gen[i]) { this.shotGen[i] = sh.gen[i]; this.shotAcc[i] = 0; }
      this.shotAcc[i] += dt * MOB_FX_BUDGET.shotSparkPerSec * this.scale;
      const x = sh.prevX[i] + (sh.x[i] - sh.prevX[i]) * alpha, y = sh.prevY[i] + (sh.y[i] - sh.prevY[i]) * alpha, z = sh.prevZ[i] + (sh.z[i] - sh.prevZ[i]) * alpha;
      while (this.shotAcc[i] >= 1) {
        this.shotAcc[i] -= 1;
        const a = this.rnd() * Math.PI * 2, s = 0.3 + this.rnd() * 0.6;
        if (this.rnd() < 0.7) this.spark(x, y, z, Math.sin(a) * s, -0.2 + this.rnd() * 0.5, Math.cos(a) * s, this.rnd() < 0.5 ? pal.main : pal.core, 0.12 + this.rnd() * 0.06, 0.25 + this.rnd() * 0.2);
        else this.dust(x, y, z, Math.sin(a) * 0.2, Math.cos(a) * 0.2, pal.dust, 0.45, 0.22);
      }
    }
  }

  /**
   * 투사체: 굵은 꼬리 + 가늘고 긴 잔광 꼬리(속도 방향 줄) + 후광 + 빠르게 도는 별 머리 + 흰 심, 착탄 지점에는 가까워질수록 커지는 그림자 점.
   * 그림자 점은 경고 원(telegraph.ts) 안쪽에 앉아 "저기로 떨어진다"를 이어 준다. 반경 0.25 → 0.95m, 짙기 0.25 → 0.8.
   */
  private drawShots(view: GameView, alpha: number): void {
    const sh = view.mobShots;
    if (!sh) return;
    const pal = this.pal;
    for (let i = 0; i < MOB_SHOT_CAPACITY; i++) {
      if (!sh.alive[i]) continue;
      const x = sh.prevX[i] + (sh.x[i] - sh.prevX[i]) * alpha, y = sh.prevY[i] + (sh.y[i] - sh.prevY[i]) * alpha, z = sh.prevZ[i] + (sh.z[i] - sh.prevZ[i]) * alpha;
      let dx = sh.x[i] - sh.prevX[i], dy = sh.y[i] - sh.prevY[i], dz = sh.z[i] - sh.prevZ[i];
      let l = Math.hypot(dx, dy, dz);
      if (l < 1e-5) { dx = sh.targetX[i] - x; dy = -0.5; dz = sh.targetZ[i] - z; l = Math.hypot(dx, dy, dz) || 1; }
      dx /= l; dy /= l; dz /= l;
      const pr = sh.progress[i];
      // 줄의 머리(밝은 끝)가 투사체 위치, 몸통은 진행 반대쪽으로 늘어난다
      this.stretch.push(x, y, z, dx, dy, dz, 2.6, 0.55, pal.main, 0.95);
      this.stretch.push(x, y, z, dx, dy, dz, 4.4, 0.2, pal.core, 0.5);
      this.heads.push(x, y, z, 1.55, 0, pal.main, 0.55);
      this.heads.push(x, y, z, 0.95, pr * 44 + i, pal.core, 1);
      this.heads.push(x, y, z, 0.42, 0, 0xffffff, 1);
      this.decal.push(DECAL_SHADOW, sh.targetX[i], sh.targetZ[i], 0.25 + 0.7 * pr * pr, 0x0c0806, 0.25 + 0.55 * pr, 0, 0);
    }
  }

  /** 정예 접두: 보호막 구체(잔량 비례) · 분열 균열(맥동). 신속 바람 줄은 emitAffixes가 입자로 낸다 */
  private drawAffixes(view: GameView, alpha: number): void {
    const m = view.mobs;
    if (!m.eliteAffix) return;
    const B = MOB_FX_BUDGET, hero = view.hero;
    const cap = this.cap(B.affixMobs);
    let used = 0;
    for (let i = 0; i < m.capacity && used < cap; i++) {
      if (INACTIVE(m.state[i]) || !m.elite[i]) continue;
      const aff = m.eliteAffix[i];
      if (aff !== 2 && aff !== 3) continue;
      const x = this.mobX(m, i, alpha), z = this.mobZ(m, i, alpha);
      if (Math.hypot(x - hero.x, z - hero.z) > B.affixRange) continue;
      const h = TARGET_H[m.kind[i]] ?? 1.5;
      if (aff === 2) {
        const frac = m.shieldFrac ? m.shieldFrac[i] : 0;
        if (frac <= 0.001) continue;
        used++;
        this.shield.push(x, m.y[i] + h * 0.52, z, h * 0.66, AFFIX_COLOR[2], 0.3 + 0.7 * frac, this.time + i);
      } else {
        used++;
        this.decal.push(DECAL_CRACK, x, z, 0.75 + h * 0.3, AFFIX_COLOR[3], 0.7 + 0.3 * Math.sin(this.time * 5 + i), 0, i * 0.137);
      }
    }
  }

  // ---- 수명 ---------------------------------------------------------------------------------------

  /** 셰이더 미리 컴파일용: 모든 풀을 보이게 둔다 */
  prewarm(on: boolean): void {
    this.arc.prewarm(on); this.ring.prewarm(on); this.pillar.prewarm(on);
    for (const p of [this.mist, this.glint, this.shard]) p.mesh.count = on ? 1 : p.n;
    this.stretch.prewarm(on); this.heads.prewarm(on); this.shield.prewarm(on); this.decal.prewarm(on);
  }

  clear(): void {
    this.arc.clear(); this.ring.clear(); this.pillar.clear();
    this.mist.clear(); this.glint.clear(); this.shard.clear();
    this.stretch.clear(); this.heads.clear(); this.shield.clear(); this.decal.clear();
    this.dashMob.fill(-1); this.runeMob.fill(-1); this.runeSlot.fill(-1); this.windAcc.fill(0); this.shotAcc.fill(0);
  }

  dispose(): void {
    this.arc.dispose(); this.ring.dispose(); this.pillar.dispose();
    for (const p of [this.mist, this.glint, this.shard]) { p.mesh.geometry.dispose(); (p.mesh.material as THREE.Material).dispose(); p.mesh.dispose(); }
    this.stretch.dispose(); this.heads.dispose(); this.shield.dispose(); this.decal.dispose();
    this.noise.dispose();
  }
}

